import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from 'yup';
import { useFormik } from 'formik';

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
//Import Flatepicker
import 'flatpickr/dist/themes/material_blue.css';

import {
  addNewBenefit as onAddNewBenefit,
  updateBenefit as onUpdateBenefit,
} from 'store/actions';

import { BenefitId, BillingName } from './BenefitCol';
import { useMutation, useQueryClient } from 'react-query';

//redux
import { useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
// import BenefitsModal from "./BenefitsModal";

import { Col, Row, Card, CardBody } from 'reactstrap';
import moment from 'moment';
import { ToastContainer } from 'react-toastify';
import { fetchAuditsApi, fetchAdvancedAuditsApi } from 'api/setup';
import { formatDateTime } from 'utils/dates';

// Utility function to remove empty fields
const removeEmptyFields = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
  );
};

function Audits() {
  const [pages, setPages] = useState(0);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
    searchColumns: [],
  });

  const [searchParams, setSearchParams] = useState({
    principal: '',
    shofcoNumber: '',
    mobile: '',
  });

  const {
    mutate: mutateAudits,
    isLoading: isLoadingAudits,
    data: audits,
  } = useMutation(
    (payload) => {
      // Construct the base payload
      const basePayload = {
        fromDate: '2024-01-01',
        toDate: moment().format('YYYY-MM-DD'),
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
        sortBy: payload.sortBy,
        ascending: payload.ascendingSort,
      };

      // If any searchParams are provided, include only non-empty ones
      if (searchParams.principal || searchParams.shofcoNumber || searchParams.mobile) {
        const cleanedSearchParams = removeEmptyFields(searchParams);
        return fetchAdvancedAuditsApi({
          ...basePayload,
          ...cleanedSearchParams,
        });
      }
      return fetchAuditsApi(payload);
    },
    {
      onSuccess: (res) => {
        res.pages > 0 && setPages(res.pages);
      },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-audits');
      },
    },
  );

  useEffect(() => {
    mutateAudits({
      ...pagination,
      pageNumber: pagination.pageNumber - 1,
    });
  }, [pagination, searchParams]);

  const onChangePagination = ({ page, limit }, filters) => {
    const searchColumns = isEmpty(filters)
      ? []
      : [
          ...(filters?.principal ? [{ name: 'principal', value: filters.principal }] : []),
          ...(filters?.shofcoNumber ? [{ name: 'shofcoNumber', value: filters.shofcoNumber }] : []),
          ...(filters?.mobile ? [{ name: 'mobile', value: filters.mobile }] : []),
        ];

    setSearchParams({
      principal: filters?.principal || '',
      shofcoNumber: filters?.shofcoNumber || '',
      mobile: filters?.mobile || '',
    });

    setPagination({
      ...pagination,
      pageNumber: page || pagination.pageNumber,
      pageSize: limit || pagination.pageSize,
      searchColumns,
    });
  };

  const toggle = () => {
    if (modal) {
      setModal(false);
      setBenefit(null);
    } else {
      setModal(true);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'timestamp',
        filterable: true,
        Cell: ({ value }) => {
          const formattedDate = value
            ? formatDateTime(value)
            : '-';
          return <span>{formattedDate}</span>;
        },
      },
      {
        Header: 'Principal',
        accessor: 'principal',
        filterable: true,
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
      {
        Header: 'Event',
        accessor: 'type',
        filterable: true,
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
      {
        Header: 'SHOFCO Number',
        accessor: 'data.shofcoNumber',
        filterable: true,
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
      {
        Header: 'Mobile',
        accessor: 'data.mobile',
        filterable: true,
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
      {
        Header: 'Member ID',
        accessor: 'data.memberId',
        filterable: true,
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
    ],
    [],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="Audit Logs" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={audits?.list?.reverse() || []}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    isPagination={true}
                    filterable={true}
                    hasSearch
                    resource="auditLogs"
                    pages={pages}
                    iscustomPageSizeOptions={true}
                    addText=""
                    loading={isLoadingAudits}
                    tableClass="align-middle table-nowrap table-check"
                    theadClass="table-light"
                    pagination="pagination pagination-rounded justify-content-end mb-2"
                    paginationData={pagination}
                    onChangePagination={onChangePagination}
                    searchByNames={[
                      {
                        value: 'principal',
                        text: t('Principal Email'),
                      },
                      {
                        value: 'shofcoNumber',
                        text: t('SHOFCO ID'),
                      },
                      {
                        value: 'mobile',
                        text: t('Mobile Number'),
                      },
                    ]}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
}

Audits.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default Audits;